import { createContext, useContext, useState, ReactNode } from 'react';
import { useUIPreferences } from '../hooks/useUIPreferences';
import { useOptionalAuthContext } from './AuthContext';

// Define the context type
interface UIPreferencesContextType {
  preferences: {
    projectsViewMode: 'grid' | 'list';
    specsViewMode: 'grid' | 'list';
    artifactsViewMode: 'grid' | 'list';
    personasViewMode: 'grid' | 'list';
    theme: 'light' | 'dark' | 'system';
    sidebarCollapsed: boolean;
    layoutMode: 'standard' | 'wide';
    jsonExpandedByDefault: boolean;
    /** Dismissal ledger for one-off onboarding/explainer panels (task 526). */
    dismissedPanels: Record<string, boolean>;
    /** Legacy browser-wide Assistant sidebar state (task 720) — no longer read (task 1051). */
    assistantSidebar: 'open' | 'collapsed';
    /** Assistant sidebar open/collapsed per User, keyed by Keycloak sub (task 1051). */
    assistantSidebarByUser: Record<string, 'open' | 'collapsed'>;
    /** Docked assistant sidebar width in px (task 720). */
    assistantSidebarWidth: number;
    /** Legacy browser-wide History collapsed flag (task 1202) — no longer read (task 1210). */
    assistantHistoryCollapsed: boolean;
    /** History section collapsed + list height per User, keyed by Keycloak sub (task 1210). */
    assistantHistoryByUser: Record<string, { collapsed: boolean; height: number }>;
  };
  updatePreference: <K extends keyof UIPreferencesContextType['preferences']>(
    key: K,
    value: UIPreferencesContextType['preferences'][K]
  ) => void;
  updatePreferences: (updates: Partial<UIPreferencesContextType['preferences']>) => void;
  resetPreferences: () => void;
  isLoaded: boolean;
}

// Create the context
const UIPreferencesContext = createContext<UIPreferencesContextType | undefined>(undefined);

// Provider component
interface UIPreferencesProviderProps {
  children: ReactNode;
}

export function UIPreferencesProvider({ children }: UIPreferencesProviderProps) {
  const uiPreferencesHook = useUIPreferences();

  return (
    <UIPreferencesContext.Provider value={uiPreferencesHook}>
      {children}
    </UIPreferencesContext.Provider>
  );
}

// Hook to use the context
export function useUIPreferencesContext() {
  const context = useContext(UIPreferencesContext);
  if (context === undefined) {
    throw new Error('useUIPreferencesContext must be used within a UIPreferencesProvider');
  }
  return context;
}

// Convenience hooks that use the context
export function useProjectsViewModeFromContext() {
  const { preferences, updatePreference, isLoaded } = useUIPreferencesContext();
  
  return {
    viewMode: preferences.projectsViewMode,
    setViewMode: (mode: 'grid' | 'list') => updatePreference('projectsViewMode', mode),
    isLoaded,
  };
}

export function useThemePreferenceFromContext() {
  const { preferences, updatePreference, isLoaded } = useUIPreferencesContext();
  
  return {
    theme: preferences.theme,
    setTheme: (theme: 'light' | 'dark' | 'system') => updatePreference('theme', theme),
    isLoaded,
  };
}

export function useSidebarPreferenceFromContext() {
  const { preferences, updatePreference, isLoaded } = useUIPreferencesContext();

  return {
    isCollapsed: preferences.sidebarCollapsed,
    setCollapsed: (collapsed: boolean) => updatePreference('sidebarCollapsed', collapsed),
    isLoaded,
  };
}

export function useLayoutModeFromContext() {
  const { preferences, updatePreference, isLoaded } = useUIPreferencesContext();

  return {
    layoutMode: preferences.layoutMode,
    setLayoutMode: (mode: 'standard' | 'wide') => updatePreference('layoutMode', mode),
    toggleLayoutMode: () => updatePreference('layoutMode', preferences.layoutMode === 'standard' ? 'wide' : 'standard'),
    isLoaded,
  };
}

export function useJsonExpandPreference() {
  const { preferences, updatePreference, isLoaded } = useUIPreferencesContext();

  return {
    jsonExpandedByDefault: preferences.jsonExpandedByDefault,
    toggleJsonExpand: () => updatePreference('jsonExpandedByDefault', !preferences.jsonExpandedByDefault),
    isLoaded,
  };
}

/**
 * Assistant sidebar placement preferences (task 720).
 *
 * Open/collapsed and width persist through the same localStorage-backed store
 * as `layoutMode`, so the sidebar comes back the way the User left it across
 * reloads and sessions.
 *
 * Open/collapsed is per User (task 1051), keyed by the Keycloak subject: the
 * store is browser-wide and survives logout, so a browser-wide choice made a
 * new account start collapsed because an earlier account collapsed the rail.
 * No entry means open. Without a subject the hook reports open and writes
 * nothing. Width stays browser-wide — it is a property of the monitor, not of
 * the User.
 *
 * Non-throwing outside a `UIPreferencesProvider` (the `useDismissiblePanel`
 * precedent): the sidebar is mounted by suites that render the app shell
 * without the preference provider, and a chat panel must never be the thing
 * that takes a page down. Outside the provider it degrades to in-memory
 * session state.
 */
export const ASSISTANT_SIDEBAR_MIN_WIDTH = 280;
export const ASSISTANT_SIDEBAR_MAX_WIDTH = 520;
export const ASSISTANT_SIDEBAR_DEFAULT_WIDTH = 360;

/** Keep a stored or dragged width inside the drag range. */
export const clampAssistantSidebarWidth = (width: number): number =>
  Math.min(ASSISTANT_SIDEBAR_MAX_WIDTH, Math.max(ASSISTANT_SIDEBAR_MIN_WIDTH, Math.round(width)));

export function useAssistantSidebarPreference() {
  const context = useContext(UIPreferencesContext);
  // Optional, not `useAuthContext()`: the sidebar is mounted by suites without
  // an AuthProvider, and this hook must degrade rather than throw. `sub`, not
  // `userId` — every authenticated session has a subject, while the AMS id is
  // absent on a not-yet-provisioned account.
  const sub = useOptionalAuthContext()?.user?.sub;
  const [sessionState, setSessionState] = useState<'open' | 'collapsed'>('open');
  const [sessionWidth, setSessionWidth] = useState(ASSISTANT_SIDEBAR_DEFAULT_WIDTH);

  if (!context) {
    return {
      sidebarState: sessionState,
      setSidebarState: setSessionState,
      width: sessionWidth,
      setWidth: (next: number) => setSessionWidth(clampAssistantSidebarWidth(next)),
      isLoaded: true,
    };
  }

  const { preferences, updatePreference, isLoaded } = context;
  const byUser = preferences.assistantSidebarByUser ?? {};
  return {
    sidebarState: (sub && byUser[sub]) || 'open',
    setSidebarState: (state: 'open' | 'collapsed') => {
      if (!sub) return;
      updatePreference('assistantSidebarByUser', { ...byUser, [sub]: state });
    },
    width: clampAssistantSidebarWidth(
      preferences.assistantSidebarWidth || ASSISTANT_SIDEBAR_DEFAULT_WIDTH
    ),
    setWidth: (next: number) =>
      updatePreference('assistantSidebarWidth', clampAssistantSidebarWidth(next)),
    isLoaded,
  };
}

/**
 * The Assistant sidebar's History section (task 1202): open/collapsed and the
 * list height (task 1210).
 *
 * Both are per User, keyed by the Keycloak subject exactly as
 * `useAssistantSidebarPreference` keys open/collapsed (task 1051): the store is
 * browser-wide and survives logout, so one account's choice must never follow
 * the next account in a shared browser, and a User finds the section as they
 * left it after signing back in. No entry means open at the default height.
 * Without a subject the hook reports open at the default height and writes
 * nothing. The height is clamped at read time.
 *
 * Non-throwing outside a `UIPreferencesProvider`, for the same reason as
 * `useAssistantSidebarPreference`: the conversation is mounted by suites and
 * pages without the provider, and there it degrades to in-memory session state.
 */
export const ASSISTANT_HISTORY_MIN_HEIGHT = 88;
export const ASSISTANT_HISTORY_MAX_HEIGHT = 640;
export const ASSISTANT_HISTORY_DEFAULT_HEIGHT = 176;
/** One History row (title + time line + spacing): the keyboard step. */
export const ASSISTANT_HISTORY_ROW_HEIGHT = 44;

/** Keep a stored or dragged History list height inside the preference range. */
export const clampAssistantHistoryHeight = (height: number): number =>
  Math.min(ASSISTANT_HISTORY_MAX_HEIGHT, Math.max(ASSISTANT_HISTORY_MIN_HEIGHT, Math.round(height)));

export function useAssistantHistoryPreference() {
  const context = useContext(UIPreferencesContext);
  // `sub`, not `userId`, and the optional context: see `useAssistantSidebarPreference`.
  const sub = useOptionalAuthContext()?.user?.sub;
  const [sessionCollapsed, setSessionCollapsed] = useState(false);
  const [sessionHeight, setSessionHeight] = useState(ASSISTANT_HISTORY_DEFAULT_HEIGHT);

  if (!context) {
    return {
      collapsed: sessionCollapsed,
      setCollapsed: setSessionCollapsed,
      height: sessionHeight,
      setHeight: (next: number) => setSessionHeight(clampAssistantHistoryHeight(next)),
    };
  }

  const { preferences, updatePreference } = context;
  const byUser = preferences.assistantHistoryByUser ?? {};
  const stored = sub ? byUser[sub] : undefined;
  const collapsed = stored?.collapsed === true;
  const height = clampAssistantHistoryHeight(
    typeof stored?.height === 'number' && Number.isFinite(stored.height)
      ? stored.height
      : ASSISTANT_HISTORY_DEFAULT_HEIGHT
  );
  const write = (next: Partial<{ collapsed: boolean; height: number }>) => {
    if (!sub) return;
    updatePreference('assistantHistoryByUser', { ...byUser, [sub]: { collapsed, height, ...next } });
  };
  return {
    collapsed,
    setCollapsed: (next: boolean) => write({ collapsed: next }),
    height,
    setHeight: (next: number) => write({ height: clampAssistantHistoryHeight(next) }),
  };
}

/**
 * Per-User dismissal for a one-off onboarding/explainer panel (task 526).
 *
 * Backed by the same UI-preference store as the user-menu toggles, so a
 * dismissal survives reloads. Reads the whole map from the single provider
 * state rather than a per-component copy — several dismissible panels can be
 * mounted at once and each must merge into, not overwrite, the ledger.
 *
 * Non-throwing outside a UIPreferencesProvider (the `useOptionalAuthContext`
 * precedent): the component test suite renders these panels without the
 * provider, so the hook degrades to in-memory session state instead of
 * crashing the surface it decorates.
 */
export function useDismissiblePanel(panelKey: string) {
  const context = useContext(UIPreferencesContext);
  const [sessionDismissed, setSessionDismissed] = useState(false);

  if (!context) {
    return {
      isDismissed: sessionDismissed,
      dismiss: () => setSessionDismissed(true),
      isLoaded: true,
    };
  }

  const { preferences, updatePreference, isLoaded } = context;
  return {
    isDismissed: preferences.dismissedPanels[panelKey] === true,
    dismiss: () =>
      updatePreference('dismissedPanels', {
        ...preferences.dismissedPanels,
        [panelKey]: true,
      }),
    isLoaded,
  };
}